/**
 * Platform transport boundary — the approval inbox, the signed-in user's
 * notifications, and the company's branches.
 *
 * Concurrency and idempotency, verified against
 * openapi/generated/route_policy.gen.go:
 *   - decide / delegate / mark-read / mark-all-read / create branch are
 *     `client-key` idempotent and MUST carry an Idempotency-Key.
 *   - PATCH /branches/{id} takes a REQUIRED strong If-Match.
 *   - Notification preferences are replaced whole; an omitted entry is
 *     deleted, so the screen always sends the complete set.
 *
 * None of these routes is branch-scoped: approvals carry the branch of the
 * document they are about, notifications carry their own, and a branch is the
 * dimension itself. So every call uses the plain (company) http helpers.
 */
import type { components } from "@erp/api-client";

import { apiFetch, apiRequest, type Page } from "@/lib/api/http";
import { collectCursorPages, fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type Approval = Schemas["Approval"];
export type ApprovalDetail = Schemas["ApprovalDetail"];
export type ApprovalTask = Schemas["ApprovalTask"];
export type ApprovalEvent = Schemas["ApprovalEvent"];
export type ApprovalStatus = Schemas["ApprovalStatus"];
export type Notification = Schemas["Notification"];
export type NotificationPage = Schemas["NotificationPage"];
export type NotificationPreference = Schemas["NotificationPreference"];
export type Branch = Schemas["Branch"];
export type BranchCreate = Schemas["BranchCreate"];
export type BranchPatch = Schemas["BranchPatch"];
export type BranchStatus = Schemas["BranchStatus"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

export const APPROVAL_STATUSES: readonly ApprovalStatus[] = [
  "pending",
  "approved",
  "rejected",
  "cancelled",
];

// ── Approvals ────────────────────────────────────────────────────────────

export interface ApprovalFilters {
  /** "me" narrows the page to the caller's own inbox. */
  assignee?: string;
  status?: ApprovalStatus;
  request_type?: string;
  entity_type?: string;
  entity_id?: string;
}

/** `listApprovals` — GET · platform.approval.read. An inbox item carries the
 *  caller's own open task, so an approver can act without a second round trip. */
export function listApprovals(
  filters: ApprovalFilters,
  limit: number,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<Page<Approval>> {
  return apiFetch<Page<Approval>>("/approvals", {
    query: { ...filters, limit, ...(cursor === undefined ? {} : { cursor }) },
    signal,
  });
}

/** `getApproval` — GET · platform.approval.read. Publishes the whole task and
 *  event trail; the list's `pending_task` is null here by design. */
export function getApproval(approvalId: string, signal?: AbortSignal): Promise<ApprovalDetail> {
  return apiFetch<ApprovalDetail>(`/approvals/${encodeURIComponent(approvalId)}`, { signal });
}

/** `decideApproval` — POST · platform.approval.decide · client-key. */
export function decideApproval(
  approvalId: string,
  decision: "approve" | "reject",
  comment: string | undefined,
  idempotencyKey: string,
): Promise<ApprovalDetail> {
  return apiFetch<ApprovalDetail>(`/approvals/${encodeURIComponent(approvalId)}/decision`, {
    method: "POST",
    body: comment === undefined || comment === "" ? { decision } : { decision, comment },
    idempotencyKey,
  });
}

/** `delegateApproval` — POST · platform.approval.delegate · client-key. The
 *  receiver must be another user of the company, never the requester. */
export function delegateApproval(
  approvalId: string,
  delegateTo: string,
  comment: string | undefined,
  idempotencyKey: string,
): Promise<ApprovalDetail> {
  return apiFetch<ApprovalDetail>(`/approvals/${encodeURIComponent(approvalId)}/delegate`, {
    method: "POST",
    body:
      comment === undefined || comment === ""
        ? { delegate_to: delegateTo }
        : { delegate_to: delegateTo, comment },
    idempotencyKey,
  });
}

// ── Notifications ────────────────────────────────────────────────────────

/** `listNotifications` — GET · platform.notification.read. Self-scoped in SQL:
 *  a caller cannot read another user's rows whatever the query says. */
export function listNotifications(
  unread: boolean,
  limit: number,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<NotificationPage> {
  return apiFetch<NotificationPage>("/notifications", {
    query: {
      limit,
      ...(unread ? { unread: true } : {}),
      ...(cursor === undefined ? {} : { cursor }),
    },
    signal,
  });
}

/** `markNotificationRead` — POST · platform.notification.manage · client-key. */
export function markNotificationRead(notificationId: string, idempotencyKey: string) {
  return apiFetch<{ id: string; read_at: string }>(
    `/notifications/${encodeURIComponent(notificationId)}/read`,
    { method: "POST", idempotencyKey },
  );
}

/** `markAllNotificationsRead` — POST · platform.notification.manage ·
 *  client-key. Answers how many rows actually changed. */
export function markAllNotificationsRead(idempotencyKey: string) {
  return apiFetch<{ marked: number }>("/notifications/read-all", {
    method: "POST",
    idempotencyKey,
  });
}

export function getNotificationPreferences(signal?: AbortSignal) {
  return apiFetch<{ items: readonly NotificationPreference[] }>("/notification-preferences", {
    signal,
  });
}

/** `replaceNotificationPreferences` — PUT · platform.notification.manage. The
 *  supplied set becomes the stored set; anything omitted is deleted. */
export function replaceNotificationPreferences(items: NotificationPreference[]) {
  return apiFetch<{ items: readonly NotificationPreference[] }>("/notification-preferences", {
    method: "PUT",
    body: { items },
  });
}

// ── Branches ─────────────────────────────────────────────────────────────

export function listBranches(
  query: { status?: BranchStatus } = {},
  signal?: AbortSignal,
): Promise<Branch[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<Page<Branch>>("/branches", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getBranch(branchId: string, signal?: AbortSignal): Promise<WithEtag<Branch>> {
  return fetchWithEtag<Branch>(`/branches/${encodeURIComponent(branchId)}`, { signal });
}

/** `createBranch` — POST · platform.branch.write · client-key. The code is
 *  immutable once set: legal numbering masks are built from it. */
export function createBranch(input: BranchCreate, idempotencyKey: string): Promise<Branch> {
  return apiFetch<Branch>("/branches", { method: "POST", body: input, idempotencyKey });
}

/** `updateBranch` — PATCH · platform.branch.write · If-Match required. */
export async function updateBranch(
  branchId: string,
  patch: BranchPatch,
  ifMatch: string,
): Promise<WithEtag<Branch>> {
  const { data, etag } = await apiRequest<Branch>(`/branches/${encodeURIComponent(branchId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}
